import { FiX } from "react-icons/fi";
import styled from "styled-components";

import { currencySymbols } from "../../variable";
import {
  ACCENT_800,
  DAVYS_GREY_100,
  DAVYS_GREY_200,
  DAVYS_GREY_500,
  DAVYS_GREY_600,
  DAVYS_GREY_800,
  ERROR_RED_800,
  white,
} from "./colors";
import FlexBox from "./FlexBox";
import { Body2, Support } from "./Headings";

const Wrapper = styled(FlexBox)`
  width: ${({ width }) => width || "100%"};
  position: relative;
  column-gap: 0.5rem;
  align-items: center;
  border-radius: 0.5rem;
  box-sizing: border-box;
  background-color: ${({ isDisabled }) =>
    isDisabled ? DAVYS_GREY_200 : DAVYS_GREY_100};
  padding: ${({ padding }) => padding};
  border: 1px solid ${({ theme }) => theme?.input?.border};
  &:focus-within {
    border-color: ${DAVYS_GREY_800};
  }
`;

export const InputBox = styled.input`
  flex: 1;
  width: ${({ width }) => width || "auto"};
  margin: 0;
  padding: ${({ padding }) => padding || 0};
  border: none;
  outline: none;
  font-family: "Quicksand";
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: ${({ fontWeight }) => fontWeight || 500};
  color: ${DAVYS_GREY_800};
  background: ${({ disabled }) => (disabled ? DAVYS_GREY_200 : DAVYS_GREY_100)};
  ::placeholder {
    color: ${DAVYS_GREY_600};
  }
  ::-webkit-calendar-picker-indicator {
    cursor: pointer;
  }
`;

const CloseIconWrapper = styled(FlexBox)`
  width: 1.5rem;
  height: 1.5rem;
  background-color: ${white};
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const RequiredIndicator = styled.span`
  color: ${({ theme }) => theme?.input?.requiredColor || ACCENT_800};
`;

const CurrencySymbol = styled(FlexBox)`
  position: absolute;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  bottom: 0;
  padding: 0.75rem;
  border-right: 1px solid ${DAVYS_GREY_500};
  background-color: ${DAVYS_GREY_200};
  border-top-left-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
`;

const Input = ({
  label,
  error,
  type,
  placeholder,
  showCross,
  required = false,
  disabled = false,
  value,
  inputMode,
  onChange = () => {},
  onFocus,
  onSubmit,
  onKeyDown,
  onCrossIconClick,
  icon: Icon,
  onIconClick,
  theme,
  onBlur,
  width,
  isLarge = true,
  padding = isLarge ? "0.75rem" : "0.5rem",
  wrapperWidth,
  readOnly = false,
  max,
  min,
  labelBold = false,
  currency = null,
  id = "",
}) => {
  const handleKeyDown = e => {
    if (e.keyCode === 13) {
      onSubmit?.();
    }
  };

  return (
    <FlexBox column rowGap="0.5rem" width={wrapperWidth} id={id}>
      {label && (
        <Body2 bold={labelBold}>
          {label}
          {required && <RequiredIndicator>*</RequiredIndicator>}
        </Body2>
      )}
      <Wrapper
        theme={theme}
        width={width}
        padding={padding}
        isDisabled={disabled}
      >
        {currency && (
          <CurrencySymbol>
            <Body2 color={DAVYS_GREY_600}>{currencySymbols[currency]}</Body2>
          </CurrencySymbol>
        )}
        <InputBox
          disabled={disabled}
          value={value}
          type={type}
          inputMode={inputMode}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown || handleKeyDown}
          theme={theme}
          readOnly={readOnly}
          max={max}
          min={min}
        />
        {showCross && !!value && (
          <CloseIconWrapper onClick={onCrossIconClick}>
            <FiX color={theme?.input?.crossIconColor} strokeWidth={3} />
          </CloseIconWrapper>
        )}
        {Icon && (
          <Icon
            color={theme?.input?.IconColor}
            size="1.375rem"
            onClick={onIconClick}
          />
        )}
      </Wrapper>
      {error && <Support color={ERROR_RED_800}>{error}</Support>}
    </FlexBox>
  );
};

Input.defaultProps = {
  theme: {
    input: {
      border: DAVYS_GREY_500,
      requiredColor: ACCENT_800,
      IconColor: ACCENT_800,
      crossIconColor: ACCENT_800,
    },
  },
};

export default Input;
